/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
import {
  FloatingFocusManager,
  FloatingOverlay,
  FloatingPortal,
  useClick,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import classNames from 'classnames';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Button from '../Button/Button';
import { ButtonVariants } from '../Button/Button.types';
import Circle from './Circle.svg';
import FilledCircle from './FilledCircle.svg';
import styles from './Wizard.module.css';
import { StepCount, WizardProps, WizardStep } from './Wizard.types';
import { useTranslation } from 'react-i18next';

const Wizard: FC<WizardProps> = ({
  isOpen,
  handleClose,
  closeButtonText,
  className,
  steps,
  currentStep,
  showStepIndicator = true,
  ...props
}) => {
  const { t } = useTranslation();
  const closeButtonRef = useRef(null);
  const { floating, context } = useFloating({
    open: isOpen,
    onOpenChange: handleClose,
  });

  const click = useClick(context);
  const role = useRole(context);

  const [activeStep, setActiveStep] = useState<StepCount>(currentStep || 0);

  useEffect(() => {
    if (currentStep !== undefined) setActiveStep(currentStep);
  }, [currentStep]);

  const { getFloatingProps } = useInteractions([click, role]);

  const currentStepData = useMemo(() => steps[activeStep] as WizardStep, [steps, activeStep]);

  const handleNextClick = useCallback(() => {
    if (currentStepData?.onNextClick) {
      currentStepData?.onNextClick();
    } else {
      setActiveStep((stepNumber) => {
        const proposedNextStep = stepNumber + 1;
        if (proposedNextStep > 6) return 6 as StepCount;
        return proposedNextStep as StepCount;
      });
    }
  }, [currentStepData]);
  const handleBackClick = useCallback(() => {
    if (currentStepData?.onBackClick) {
      currentStepData?.onBackClick();
    } else {
      setActiveStep((stepNumber) => {
        const proposedPrevStep = stepNumber - 1;
        if (proposedPrevStep < 0) return 0 as StepCount;
        return proposedPrevStep as StepCount;
      });
    }
  }, [currentStepData]);

  return (
    <FloatingPortal>
      {isOpen && (
        <FloatingOverlay
          className={styles.overlay}
          lockScroll
          onPointerEnterCapture={() => {}}
          onPointerLeaveCapture={() => {}}
        >
          <FloatingFocusManager context={context} initialFocus={closeButtonRef}>
            <div
              className={classNames(styles.wizard, { [className!]: className !== undefined })}
              ref={floating}
              data-testid="wizard"
              {...getFloatingProps()}
              {...props}
            >
              <div className={styles.header}>
                <h1 className={styles.title}>{currentStepData?.title}</h1>
                {showStepIndicator && (
                  <div className={styles.stepIndicatorMessage}>
                    {t('wizard.currentStep', { active: activeStep + 1, total: steps.length })}
                  </div>
                )}
                <div className={styles.description}>{currentStepData?.description}</div>
              </div>
              <div className={styles.body}>{currentStepData?.content}</div>
              <div className={styles.footer}>
                <div className={styles.backButtonContainer}>
                  <Button
                    className={styles.backButton}
                    onClick={handleBackClick}
                    disabled={currentStepData?.backButtonDisabled}
                    type="button"
                    isLoading={currentStepData?.backButtonIsLoading}
                    variant={ButtonVariants.Secondary}
                  >
                    {currentStepData?.backButtonText || t('common.back')}
                  </Button>
                </div>
                {showStepIndicator && (
                  <div
                    className={styles.stepTracker}
                    tabIndex={0}
                    aria-label={t('wizard.currentStep', { active: activeStep + 1, total: steps.length })}
                  >
                    {steps.map((step, index) =>
                      index <= activeStep ? (
                        <img src={FilledCircle} key={step.description} alt="" />
                      ) : (
                        <img src={Circle} key={step.description} alt="" />
                      ),
                    )}
                  </div>
                )}
                <div className={styles.nextButtonContainer}>
                  <Button
                    className={styles.nextButton}
                    onClick={handleNextClick}
                    disabled={currentStepData?.nextButtonDisabled}
                    type="button"
                    isLoading={currentStepData?.nextButtonIsLoading}
                  >
                    {currentStepData?.nextButtonText || t('common.next')}
                  </Button>
                </div>
              </div>
              <Button
                className={styles.closeButton}
                variant={ButtonVariants.Text}
                ref={closeButtonRef}
                onClick={handleClose}
                type="button"
              >
                {closeButtonText || t('common.exit')}
              </Button>
            </div>
          </FloatingFocusManager>
        </FloatingOverlay>
      )}
    </FloatingPortal>
  );
};

export default Wizard;
